import type { CartItem } from "@/domain/cart/cart-item";

export type PriceKind = "retail" | "wholesale";
export type WholesaleQualificationReason = "cart_quantity" | "product_minimum";

export const wholesaleOrderMinimumItemCount = 10;

export interface CartLinePrice {
  unitPrice: number;
  subtotal: number;
  priceKind: PriceKind;
}

export interface WholesaleQualification {
  readonly isWholesaleOrder: boolean;
  readonly reason: WholesaleQualificationReason | null;
}

export interface WholesaleProgress {
  readonly kind: "cart_quantity" | "product_minimum";
  readonly missingQuantity: number;
  readonly productName?: string;
}

export interface CartPricing {
  readonly qualification: WholesaleQualification;
  readonly linePrices: readonly CartLinePrice[];
  readonly subtotalRetail: number;
  readonly subtotalEstimated: number;
  readonly wholesaleDiscount: number;
  readonly itemCount: number;
  readonly wholesaleProgress: WholesaleProgress | null;
}

export function calculateCartPricing(items: readonly CartItem[]): CartPricing {
  const qualification = evaluateWholesaleQualification(items);
  const linePrices = items.map((item) => calculateCartLinePrice(item, qualification.isWholesaleOrder));
  const subtotalRetail = items.reduce((total, item) => total + item.salePrice * item.quantity, 0);
  const subtotalEstimated = linePrices.reduce((total, price) => total + price.subtotal, 0);

  return {
    qualification,
    linePrices,
    subtotalRetail,
    subtotalEstimated,
    wholesaleDiscount: subtotalRetail - subtotalEstimated,
    itemCount: items.reduce((total, item) => total + item.quantity, 0),
    wholesaleProgress: qualification.isWholesaleOrder ? null : findWholesaleProgress(items),
  };
}

export function calculateCartLinePrice(
  item: CartItem,
  isWholesaleOrder = false,
): CartLinePrice {
  const usesWholesale = isWholesaleOrder && item.wholesalePrice !== null;

  const unitPrice = usesWholesale ? item.wholesalePrice ?? item.salePrice : item.salePrice;

  return {
    unitPrice,
    subtotal: unitPrice * item.quantity,
    priceKind: usesWholesale ? "wholesale" : "retail",
  };
}

export function evaluateWholesaleQualification(
  items: readonly CartItem[],
): WholesaleQualification {
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);

  if (itemCount >= wholesaleOrderMinimumItemCount) {
    return { isWholesaleOrder: true, reason: "cart_quantity" };
  }

  const qualifyingItem = items.find(
    (item) =>
      item.wholesalePrice !== null &&
      item.wholesaleMinQuantity !== null &&
      item.quantity >= item.wholesaleMinQuantity,
  );

  return qualifyingItem
    ? { isWholesaleOrder: true, reason: "product_minimum" }
    : { isWholesaleOrder: false, reason: null };
}

function findWholesaleProgress(items: readonly CartItem[]): WholesaleProgress {
  const itemCount = items.reduce((total, item) => total + item.quantity, 0);
  const cartProgress: WholesaleProgress = {
    kind: "cart_quantity",
    missingQuantity: wholesaleOrderMinimumItemCount - itemCount,
  };
  let closestProductProgress: WholesaleProgress | null = null;

  for (const item of items) {
    if (item.wholesalePrice === null || item.wholesaleMinQuantity === null) {
      continue;
    }

    const missingQuantity = item.wholesaleMinQuantity - item.quantity;

    if (
      missingQuantity > 0 &&
      (!closestProductProgress || missingQuantity < closestProductProgress.missingQuantity)
    ) {
      closestProductProgress = {
        kind: "product_minimum",
        missingQuantity,
        productName: item.name,
      };
    }
  }

  return closestProductProgress &&
    closestProductProgress.missingQuantity < cartProgress.missingQuantity
    ? closestProductProgress
    : cartProgress;
}
